import { rubyService } from "@/services";
import { ChannelType } from "@redbox-ruby/data-lib";
import clamp from "lodash/clamp";
import moment from "moment";
import { put, takeEvery } from "redux-saga/effects";
import { type ExtractRequestActionRequestType } from "../actions/Action";
import { ActionName } from "../actions/ActionType";
import { getActiveBudgetPlanAction, listBudgetPlansAction } from "../actions/campaignActions";
import {
    budgetConfigActions,
    calendarActions,
    channelConfigActions,
    regionConfigActions,
    type ConfigActions,
    type UpdateRegionWeightingsActionPayload,
} from "../actions/configActions";
import { getRegionsReportAction } from "../actions/reportingActions";
import { type BudgetPlanTemplate, type TimeRange } from "../reducers/domain";
import { selectCampaignActiveBudget, selectRegions } from "../selectors/campaignSelectors";
import { selectCampaignRegionConfig } from "../selectors/configSelectors";
import {
    RubyBudgetPlanMethod,
    RubyBudgetPlanPolicy,
    RubyBudgetPlanStatus,
    RubyChannelType,
    RubyPurpose,
    RubyRegionStatus,
    type RubyBasicBudgetPlan,
    type RubyCampaignId,
    type RubyRegion,
    type RubyRegionId,
    type RubyUpdateRegionResponse,
} from "../services/backend/RubyData";
import { type CountryMap } from "../types/utils";
import {
    DEFAULT_WEEKLY_OVERSPEND,
    convertTemplateToPlanDetails,
    distributeAmountByPercentages,
    extendBudgetPlans,
    findCampaignBudgetPlan,
} from "../utilities/budget";
import {
    runDataRequestAction,
    runRequestAction,
    selectAsyncDataFromState,
    selectFromState,
    takeError,
    takeRequests,
    takeSuccess,
} from "../utilities/saga";
import { delay } from "../utilities/time";
import { isRegionWithPurpose } from "../utilities/types";

export function* updateConfigSaga(action: ExtractRequestActionRequestType<ConfigActions.UpdateChannelAction>) {
    yield* runRequestAction(action, channelConfigActions.update, ({ teamId, campaignId, channelType, update }) => {
        const ids = {
            teamId,
            campaignId,
        };
        switch (channelType) {
            case RubyChannelType.APPLE_SEARCH_RESULTS: {
                return rubyService.appleChannels.updateAsrChannel(ids, update);
            }
            case RubyChannelType.APPLE_SEARCH_TABS: {
                return rubyService.appleChannels.updateAstChannel(ids, update);
            }
            case RubyChannelType.APPLE_TODAY_TABS: {
                return rubyService.appleChannels.updateAttChannel(ids, update);
            }
            case RubyChannelType.PRODUCT_PAGE_BROWSE: {
                return rubyService.appleChannels.updatePpbChannel(ids, update);
            }
        }
    });
}

export function calculateRegionWeightings(
    channelType: RubyChannelType,
    regions: RubyRegion[],
    countryWeights: CountryMap<number>,
    discoveryWeight: number // 0 - 1
): Record<RubyRegionId, number> {
    const finalWeights: Record<RubyRegionId, number> = {};
    discoveryWeight = clamp(discoveryWeight, 0.02, 1);

    regions.forEach((region) => {
        const purpose = isRegionWithPurpose(channelType, region) ? region.purpose : null;

        let weight = countryWeights[region.country];

        if (purpose === RubyPurpose.KEYWORD_DISCOVERY) {
            weight *= 0.01;
        } else if (purpose === RubyPurpose.BID_DISCOVERY) {
            weight *= discoveryWeight - 0.01;
        } else if (purpose === RubyPurpose.BID_OPTIMISATION) {
            weight *= 1 - discoveryWeight;
        }

        finalWeights[region.id] = weight;
    });

    return finalWeights;
}

function* addCalendarSaga(action: ExtractRequestActionRequestType<ConfigActions.AddCalendarAction>) {
    yield* runRequestAction(action, calendarActions.add, async (req) => {
        const response = await rubyService.services.addCalendarAndEvents({
            data: req.data,
            calendar: { name: req.name },
        });
        return response;
    });
}

function* listCalendarsSaga(action: ExtractRequestActionRequestType<ConfigActions.ListCalendarsAction>) {
    yield* runRequestAction(action, calendarActions.list, () =>
        rubyService.services.listCalendars().then((res) => res.calendars)
    );
}

function* deleteCalendarSaga(action: ExtractRequestActionRequestType<ConfigActions.DeleteCalendarAction>) {
    yield* runRequestAction(action, calendarActions.delete, async (req) => {
        await rubyService.services.deleteCalendar({ calendarId: req });
    });
}

export function updateWeightings({
    teamId,
    campaignId,
    channelType,
    regions,
    weightings,
    discoveryWeighting,
}: UpdateRegionWeightingsActionPayload): Promise<RubyUpdateRegionResponse[]> {
    const weights = calculateRegionWeightings(channelType, regions, weightings, discoveryWeighting);
    return Promise.all(
        regions.map((region) => {
            const id = {
                teamId,
                campaignId,
                regionId: region.id,
            };
            const weight = {
                budgetWeighting: weights[region.id],
            };
            switch (channelType) {
                case RubyChannelType.APPLE_SEARCH_RESULTS:
                    return rubyService.appleChannels.updateAsrChannelRegion(id, weight);
                case RubyChannelType.APPLE_SEARCH_TABS:
                    return rubyService.appleChannels.updateAstChannelRegion(id, weight);
                case RubyChannelType.APPLE_TODAY_TABS:
                    return rubyService.appleChannels.updateAttChannelRegion(id, weight);
                case RubyChannelType.PRODUCT_PAGE_BROWSE:
                    return rubyService.appleChannels.updatePpbChannelRegion(id, weight);
                default:
                    return Promise.reject();
            }
        })
    );
}

function* createRegionsSaga(action: ExtractRequestActionRequestType<ConfigActions.CreateRegionsAction>) {
    // This is a bit of a cheat to fire off the channel update first
    // Would technically be better to wait for this to complete as part of the saga
    if (action.payload.request.channelTargeting) {
        yield put(
            channelConfigActions.update.request({
                campaignId: action.payload.request.campaignId,
                teamId: action.payload.request.teamId,
                channelType: action.payload.request.channelType,
                update: {
                    targetingDimensions: action.payload.request.channelTargeting,
                },
            })
        );
    }

    yield* runRequestAction(
        action,
        regionConfigActions.create,
        async ({ teamId, campaignId, channelType, regions, create, config, weightings, discoveryWeighting }) => {
            const newRegions = await Promise.all(
                create.map(async (country) => {
                    const id = {
                        teamId,
                        campaignId,
                    };
                    const payload = {
                        ...config,
                        status: RubyRegionStatus.ACTIVE,
                        country,
                        budgetWeighting: 0, //Must be updated later per region
                    };
                    const resp = await (() => {
                        switch (channelType) {
                            case RubyChannelType.APPLE_SEARCH_RESULTS:
                                return rubyService.appleChannels.createAsrChannelRegion(id, payload);
                            case RubyChannelType.APPLE_SEARCH_TABS:
                                return rubyService.appleChannels.createAstChannelRegion(id, payload);
                            case RubyChannelType.APPLE_TODAY_TABS:
                                return rubyService.appleChannels.createAttChannelRegion(id, payload);
                            case RubyChannelType.PRODUCT_PAGE_BROWSE:
                                return rubyService.appleChannels.createPpbChannelRegion(id, payload);
                        }
                    })();
                    return resp.results;
                })
            );

            return await updateWeightings({
                teamId,
                campaignId,
                channelType,
                regions: [...Object.values(regions).flat(), ...newRegions.flat()],
                weightings,
                discoveryWeighting,
            });
        }
    );
}

export function* updateRegionsSaga(action: ExtractRequestActionRequestType<ConfigActions.UpdateRegionsAction>) {
    yield* runRequestAction(action, regionConfigActions.update, ({ teamId, campaignId, channelType, update }) => {
        return Promise.all(
            update.map(({ id, ...region }) => {
                const payloadId = {
                    teamId,
                    campaignId,
                    regionId: id,
                };
                switch (channelType) {
                    case RubyChannelType.APPLE_SEARCH_RESULTS:
                        return rubyService.appleChannels.updateAsrChannelRegion(payloadId, region);
                    case RubyChannelType.APPLE_SEARCH_TABS:
                        return rubyService.appleChannels.updateAstChannelRegion(payloadId, region);
                    case RubyChannelType.APPLE_TODAY_TABS:
                        return rubyService.appleChannels.updateAttChannelRegion(payloadId, region);
                    case RubyChannelType.PRODUCT_PAGE_BROWSE:
                        return rubyService.appleChannels.updatePpbChannelRegion(payloadId, region);
                    default:
                        return Promise.reject();
                }
            })
        );
    });
}

export function* updateDiscoveryWeightingSaga(action: ConfigActions.UpdateDiscoveryWeightingAction) {
    const budget = yield* selectFromState((state) => selectCampaignActiveBudget(state, action.payload.campaignId));
    yield put(
        getRegionsReportAction.request({
            campaignId: action.payload.campaignId,
            teamId: action.payload.teamId,
            from: budget.data?.start,
            to: budget.data?.end,
        })
    );
    yield delay(100);

    const regions = yield* selectFromState((state) => selectRegions(state, action.payload.campaignId).data);
    const regionsConfig = yield* selectAsyncDataFromState((state) =>
        selectCampaignRegionConfig(state, action.payload.campaignId)
    );

    yield* runRequestAction(
        regionConfigActions.updateWeightings.request({
            ...action.payload,
            regions: regions,
            weightings: regionsConfig.weightings,
        }),
        regionConfigActions.updateWeightings,
        updateWeightings
    );
}

function* updateRegionWeightingsSaga(
    action: ExtractRequestActionRequestType<ConfigActions.UpdateRegionWeightingsAction>
) {
    yield* runRequestAction(action, regionConfigActions.updateWeightings, updateWeightings);
}

function* addBudgetPlanSaga(action: ExtractRequestActionRequestType<ConfigActions.AddBudgetPlanAction>) {
    yield* runRequestAction(action, budgetConfigActions.add, (req) => {
        return rubyService.budgets.createBudgetPlan(
            { campaignId: req.campaignId },
            {
                totalBudget: req.totalAmount,
                details: convertTemplateToPlanDetails(req),
                policy: req.policy,
                method: req.method,
                status: RubyBudgetPlanStatus.ACTIVE,
                start: req.timeRange[0],
                end: req.timeRange[1],
            }
        );
    });
}

function toBudgetPlanBody(plan: BudgetPlanTemplate) {
    return {
        totalBudget: plan.totalAmount,
        details: convertTemplateToPlanDetails(plan),
        policy: plan.policy,
        method: plan.method,
        status: RubyBudgetPlanStatus.ACTIVE,
        start: plan.timeRange[0],
        end: plan.timeRange[1],
    };
}

/** Budgets shared out from the team's budgets page run over a set range of dates. */
const TEAM_ALLOCATION_POLICY = RubyBudgetPlanPolicy.STOP_AT_END;

/** The body that saves a plan exactly as it already is. */
function toExistingPlanBody(plan: RubyBasicBudgetPlan) {
    return {
        totalBudget: plan.totalBudget,
        details: plan.details,
        policy: plan.policy,
        method: plan.method,
        status: plan.status,
        start: plan.start,
        end: plan.end,
    };
}

/** A plan as the budget service returns it once saved. */
type SavedBudgetPlan = Awaited<ReturnType<typeof rubyService.budgets.createBudgetPlan>>;

/**
 * A change made to one campaign while sharing out a budget, holding the way to
 * put that campaign back as it was.
 */
type CampaignBudgetWrite = {
    undo: () => Promise<unknown>;
};

/**
 * Gives one campaign its share of an amount being shared across the team.
 *
 * A campaign already running a budget keeps the plan holding it, so only the
 * amount it spends changes and its dates, policy and any weekday schedule are
 * left alone. A campaign without one is given a plan for the dates chosen.
 */
async function setCampaignBudgetAmount(
    campaignId: RubyCampaignId,
    amount: number,
    timeRange: TimeRange,
    currentPlan: RubyBasicBudgetPlan | null
): Promise<CampaignBudgetWrite & { plan: SavedBudgetPlan }> {
    if (currentPlan) {
        const savedPlan = await rubyService.budgets.updateBudgetPlan(
            { campaignId, planId: currentPlan.id },
            { ...toExistingPlanBody(currentPlan), totalBudget: amount, status: RubyBudgetPlanStatus.ACTIVE }
        );

        return {
            plan: savedPlan,
            undo: () =>
                rubyService.budgets.updateBudgetPlan(
                    { campaignId, planId: currentPlan.id },
                    toExistingPlanBody(currentPlan)
                ),
        };
    }

    const savedPlan = await rubyService.budgets.createBudgetPlan(
        { campaignId },
        toBudgetPlanBody({
            totalAmount: amount,
            policy: TEAM_ALLOCATION_POLICY,
            method: RubyBudgetPlanMethod.ADJUST_DAILY,
            timeRange,
            weeklyOverspend: null,
            weeklyBudgets: null,
        })
    );

    return {
        plan: savedPlan,
        undo: () => rubyService.budgets.deleteBudgetPlan({ campaignId, planId: savedPlan.id }),
    };
}

/**
 * Takes the budget a campaign left out of the split was running away from it.
 *
 * The whole amount goes to the campaigns sharing it, so a campaign holding a
 * budget from a previous split stops spending on top of it.
 */
async function removeCampaignBudget(
    campaignId: RubyCampaignId,
    currentPlan: RubyBasicBudgetPlan
): Promise<CampaignBudgetWrite> {
    await rubyService.budgets.deleteBudgetPlan({ campaignId, planId: currentPlan.id });

    return {
        undo: () => rubyService.budgets.createBudgetPlan({ campaignId }, toExistingPlanBody(currentPlan)),
    };
}

/**
 * Puts back the campaigns changed before a split failed.
 *
 * Each campaign is budgeted on its own, so a split that cannot be finished has
 * already changed what the campaigns before it spend. Undoing those changes in
 * the order they were made leaves the team spending the budgets it was before
 * rather than a split nobody asked for.
 */
async function undoCampaignBudgetWrites(writes: CampaignBudgetWrite[]) {
    for (const write of [...writes].reverse()) {
        try {
            await write.undo();
        } catch {
            // A campaign that won't go back is left to the refresh that follows
            // the failure to show, rather than losing the error behind it.
        }
    }
}

/** Puts the campaigns already moved onto the failed split back on their own budgets. */
async function resumeSpendingBudgets(campaignIds: RubyCampaignId[]) {
    for (const campaignId of campaignIds) {
        try {
            await rubyService.budgets.runBudgetManager({ campaignId });
        } catch {
            // As above, the campaign is left to the refresh to report.
        }
    }
}

/**
 * The plan holding each campaign's budget, read before any of them are
 * written so a campaign that can't be budgeted stops the whole split rather
 * than leaving the rest of the team half budgeted.
 */
async function readCampaignBudgetPlans(campaignIds: RubyCampaignId[], timeRange: TimeRange) {
    const plans: Array<{ campaignId: RubyCampaignId; currentPlan: RubyBasicBudgetPlan | null }> = [];

    for (const campaignId of campaignIds) {
        const campaignPlans = await rubyService.budgets.listBudgetPlans({ campaignId }, {}, { all: true });
        plans.push({ campaignId, currentPlan: findCampaignBudgetPlan(campaignPlans.results, timeRange) });
    }

    return plans;
}

export function* allocateCampaignBudgetsSaga(
    action: ExtractRequestActionRequestType<ConfigActions.AllocateCampaignBudgetsAction>
) {
    yield* runRequestAction(action, budgetConfigActions.allocate, async (req) => {
        const amounts = distributeAmountByPercentages(req.totalAmount, req.allocations);
        const plans = await readCampaignBudgetPlans(
            req.allocations.map((allocation) => allocation.campaignId),
            req.timeRange
        );
        const unallocated = await readCampaignBudgetPlans(req.unallocatedCampaignIds ?? [], req.timeRange);

        const writes: CampaignBudgetWrite[] = [];
        const savedPlans = [];
        const spending: RubyCampaignId[] = [];

        try {
            for (const [index, { campaignId, currentPlan }] of plans.entries()) {
                const write = await setCampaignBudgetAmount(campaignId, amounts[index], req.timeRange, currentPlan);
                writes.push(write);
                savedPlans.push(write.plan);
            }

            for (const { campaignId, currentPlan } of unallocated) {
                if (currentPlan) {
                    writes.push(await removeCampaignBudget(campaignId, currentPlan));
                }
            }

            // Spending moves to the new split only once every campaign in it is
            // saved, so a split that can't be finished never changes any spend.
            for (const [index, { campaignId }] of plans.entries()) {
                if (savedPlans[index].start < moment.utc().valueOf()) {
                    await rubyService.budgets.runBudgetManager({ campaignId });
                    spending.push(campaignId);
                }
            }
        } catch (err) {
            await undoCampaignBudgetWrites(writes);
            await resumeSpendingBudgets(spending);
            throw err;
        }

        return savedPlans;
    });
}

export function* refreshAllocatedCampaignPlansSaga(action: ConfigActions.AllocateCampaignBudgetsAction) {
    const campaignIds = [
        ...(action.payload.request.allocations ?? []).map((allocation) => allocation.campaignId),
        ...(action.payload.request.unallocatedCampaignIds ?? []),
    ];

    for (const campaignId of campaignIds) {
        yield put(listBudgetPlansAction.request(campaignId, true));
        yield put(getActiveBudgetPlanAction.request(campaignId, true));
    }
}

function* removeBudgetPlanSaga(action: ExtractRequestActionRequestType<ConfigActions.RemoveBudgetPlanAction>) {
    yield* runRequestAction(action, budgetConfigActions.delete, async (req) => {
        await rubyService.budgets.deleteBudgetPlan({ campaignId: req.campaignId, planId: req.id });
    });
}

export function* updateBudgetPlanSaga(action: ExtractRequestActionRequestType<ConfigActions.UpdateBudgetPlanAction>) {
    yield* runRequestAction(action, budgetConfigActions.update, async (req) => {
        const budgetPlan = await rubyService.budgets.updateBudgetPlan(
            { campaignId: req.campaignId, planId: req.id },
            {
                totalBudget: req.totalAmount,
                details: convertTemplateToPlanDetails(req),
                policy: req.policy,
                method: req.method,
                status: RubyBudgetPlanStatus.ACTIVE,
                start: req.timeRange[0],
                end: req.timeRange[1],
            }
        );

        if (budgetPlan.start < moment.utc().valueOf()) {
            await rubyService.budgets.runBudgetManager({
                campaignId: req.campaignId,
            });
        }

        return budgetPlan;
    });
}
function* updateChannelNamesSaga(action: ExtractRequestActionRequestType<ConfigActions.UpdateChannelNamesAction>) {
    yield* runRequestAction(action, channelConfigActions.updateCampaignNames, (req) => {
        const ids = {
            teamId: req.teamId,
            campaignId: req.campaignId,
        };
        switch (req.channelType) {
            case ChannelType.APPLE_SEARCH_RESULTS:
                return rubyService.appleChannels
                    .updateAsrChannelCampaignNames(ids, req.update)
                    .then((res) => res.results);
            case ChannelType.APPLE_SEARCH_TABS:
                return rubyService.appleChannels
                    .updateAstChannelCampaignNames(ids, req.update)
                    .then((res) => res.results);
            case ChannelType.APPLE_TODAY_TABS:
                return rubyService.appleChannels
                    .updateAttChannelCampaignNames(ids, req.update)
                    .then((res) => res.results);
            case ChannelType.PRODUCT_PAGE_BROWSE:
                return rubyService.appleChannels
                    .updatePpbChannelCampaignNames(ids, req.update)
                    .then((res) => res.results);
            default:
                return Promise.reject();
        }
    });
}

function* getChannelNamesSaga(action: ExtractRequestActionRequestType<ConfigActions.GetChannelNamesAction>) {
    yield* runDataRequestAction(
        action,
        channelConfigActions.getChannelNamesAction,
        (state, req) => state.config.channelNames[req.campaignId],
        async (req) => {
            const ids = { teamId: req.teamId, campaignId: req.campaignId };
            switch (req.channelType) {
                case RubyChannelType.APPLE_SEARCH_RESULTS: {
                    return rubyService.appleChannels.listAsrChannelCampaignNames(ids, {}).then((res) => res.results);
                }
                case RubyChannelType.APPLE_SEARCH_TABS: {
                    return rubyService.appleChannels.listAstChannelCampaignNames(ids, {}).then((res) => res.results);
                }
                case RubyChannelType.APPLE_TODAY_TABS: {
                    return rubyService.appleChannels.listAttChannelCampaignNames(ids, {}).then((res) => res.results);
                }
                case RubyChannelType.PRODUCT_PAGE_BROWSE: {
                    return rubyService.appleChannels.listPpbChannelCampaignNames(ids, {}).then((res) => res.results);
                }
                default:
                    return Promise.reject();
            }
        }
    );
}

function* setAllocationsSaga(action: ExtractRequestActionRequestType<ConfigActions.SetAllocationsAction>) {
    yield* runRequestAction(action, budgetConfigActions.setAllocations, async (req) => {
        await rubyService.budgets.forceBudgetAllocation(
            { campaignId: req.campaignId },
            {
                details: {
                    regions: req.regions.map((r) => ({
                        ...r,
                        // Default to BO for channel types that have no purpose
                        purpose: r.purpose ?? RubyPurpose.BID_OPTIMISATION,
                    })),
                },
                message: req.message,
            }
        );
    });
}

function* convertToRepeatingPlanSaga(
    action: ExtractRequestActionRequestType<ConfigActions.ConvertToRepeatingPlanAction>
) {
    yield* runRequestAction(action, budgetConfigActions.convertToRepeating, async ({ campaignId }) => {
        let dailyBudget = 10;

        const rubyBudgetPlans = await rubyService.budgets.listBudgetPlans(
            {
                campaignId,
            },
            {},
            { all: true }
        );

        const budgetPlans = extendBudgetPlans(rubyBudgetPlans.results);

        const activePlan = budgetPlans.find((plan) => plan.active);
        if (activePlan) {
            dailyBudget = activePlan.totalBudget / activePlan.lengthInDays;
        } else if (budgetPlans.length) {
            const lastPlan = budgetPlans.sort((a, b) => b.start - a.start)[0];
            dailyBudget = lastPlan.totalBudget / lastPlan.lengthInDays;
        }

        for (const plan of budgetPlans) {
            if (plan.status === RubyBudgetPlanStatus.ACTIVE) {
                await rubyService.budgets.deleteBudgetPlan({
                    campaignId,
                    planId: plan.id,
                });
            }
        }

        return await rubyService.budgets.createBudgetPlan(
            {
                campaignId,
            },
            {
                method: RubyBudgetPlanMethod.FIXED_DAILY,
                policy: RubyBudgetPlanPolicy.REPEATING,
                start: moment.utc().startOf("day").valueOf(),
                end: moment.utc().endOf("day").valueOf(),
                totalBudget: dailyBudget,
                details: {
                    weekdayOverspend: {
                        ...DEFAULT_WEEKLY_OVERSPEND,
                    },
                },
                status: RubyBudgetPlanStatus.ACTIVE,
            }
        );
    });
}

export default function* configSaga() {
    yield* takeRequests(ActionName.UPDATE_CHANNEL, updateConfigSaga);
    yield* takeRequests(ActionName.CREATE_REGIONS, createRegionsSaga);
    yield* takeRequests(ActionName.UPDATE_REGIONS, updateRegionsSaga);
    yield takeEvery(ActionName.UPDATE_DISCOVERY_WEIGHTING, updateDiscoveryWeightingSaga);
    yield* takeRequests(ActionName.UPDATE_REGION_WEIGHTINGS, updateRegionWeightingsSaga);
    yield* takeRequests(ActionName.ADD_BUDGET_PLAN, addBudgetPlanSaga);
    yield* takeRequests(ActionName.REMOVE_BUDGET_PLAN, removeBudgetPlanSaga);
    yield* takeRequests(ActionName.UPDATE_BUDGET_PLAN, updateBudgetPlanSaga);
    yield* takeRequests(ActionName.ALLOCATE_CAMPAIGN_BUDGETS, allocateCampaignBudgetsSaga);
    yield* takeSuccess(ActionName.ALLOCATE_CAMPAIGN_BUDGETS, refreshAllocatedCampaignPlansSaga);
    yield* takeError(ActionName.ALLOCATE_CAMPAIGN_BUDGETS, refreshAllocatedCampaignPlansSaga);
    yield* takeRequests(ActionName.UPDATE_CHANNEL_NAMES, updateChannelNamesSaga);
    yield* takeRequests(ActionName.GET_CHANNEL_NAMES, getChannelNamesSaga);
    yield* takeRequests(ActionName.SET_ALLOCATIONS, setAllocationsSaga);
    yield* takeRequests(ActionName.ADD_CALENDAR, addCalendarSaga);
    yield* takeRequests(ActionName.LIST_CALENDARS, listCalendarsSaga);
    yield* takeRequests(ActionName.DELETE_CALENDAR, deleteCalendarSaga);
    yield* takeRequests(ActionName.CONVERT_TO_REPEATING_PLAN, convertToRepeatingPlanSaga);
}
